import { useEffect, useState } from 'react';

import type { SupportBadgeResponse } from '@nexus/shared';

import { apiGet } from './api';

/**
 * The sidebar's unread counts.
 *
 * One small read per navigation — `/support/unread` for a creator,
 * `/admin/support/unread` for an operator — because a reply the creator has
 * not seen is the one thing on this site that is waiting for THEM, and a
 * number on the nav item is how they find out without opening the page on
 * spec. Refetched on every route change rather than polled: a person who is
 * clicking around is a person who would notice, and one who is not is not
 * looking at the sidebar anyway.
 *
 * Failures are swallowed. A badge that could not be read is a badge that
 * shows nothing, never a page that breaks.
 */
export function useSupportBadge(isAdmin: boolean, pathname: string): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    /* The Layout passes '' until there is a session: a visitor has no
       tickets to count, and an operator whose role is not yet known must
       not be asked for a creator's badge (a 404 on every admin page). */
    if (pathname === '') {
      setCount(0);
      return;
    }
    let cancelled = false;
    const path = isAdmin ? '/admin/support/unread' : '/support/unread';
    apiGet<SupportBadgeResponse>(path)
      .then((body) => {
        if (cancelled) return;
        /* A creator's number is the tickets with something to read; the
           operator's is the tickets needing a reply. Both are `tickets`. */
        setCount(Number.isFinite(body.tickets) ? body.tickets : 0);
      })
      .catch(() => {
        if (!cancelled) setCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, pathname]);

  return count;
}
